import { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

export const ImageGallery = ({ images = [], videos = [], title = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);

  const defaultImage = "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1200&q=80";
  const displayImages = images.length > 0 ? images : [defaultImage];
  
  // Combine images and videos for display
  const allMedia = [
    ...displayImages.map(img => ({ type: 'image', src: img })),
    ...(videos || []).map(vid => ({ type: 'video', ...vid }))
  ];

  // Fix image URL if it's a relative path
  const getImageUrl = (src) => {
    if (src.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${src}`;
    }
    return src;
  };

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % allMedia.length);
  }, [allMedia.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + allMedia.length) % allMedia.length);
  }, [allMedia.length]);

  const openZoom = () => {
    const current = allMedia[currentIndex];
    if (current.type === 'video') {
      setCurrentVideo(current);
      setShowVideoModal(true);
    } else {
      setIsZoomOpen(true);
      setZoomLevel(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const closeZoom = () => {
    setIsZoomOpen(false);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.5, 1));
    if (zoomLevel <= 1.5) {
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const currentMedia = allMedia[currentIndex];

  return (
    <div className="space-y-4" data-testid="image-gallery">
      {/* Main Display */}
      <div 
        className="gallery-main relative cursor-pointer group"
        onClick={openZoom}
      >
        {currentMedia.type === 'video' ? (
          <>
            {currentMedia.tipo === 'youtube' ? (
              <img
                src={currentMedia.thumbnail || `https://img.youtube.com/vi/${currentMedia.url.split('/').pop()}/maxresdefault.jpg`}
                alt={`${title} - Video`}
                className="w-full h-full object-cover"
              />
            ) : (
              <video
                src={getImageUrl(currentMedia.url)}
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-10 h-10 text-white ml-1" />
              </div>
            </div>
          </>
        ) : (
          <>
            <img
              src={getImageUrl(currentMedia.src)}
              alt={`${title} - Imagen ${currentIndex + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = defaultImage;
              }}
            />
            
            {/* Zoom Icon */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </>
        )}

        {/* Navigation Arrows */}
        {allMedia.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
              onClick={(e) => { e.stopPropagation(); goToPrev(); }}
              data-testid="gallery-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              data-testid="gallery-next"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </>
        )}

        {/* Counter */}
        <div className="absolute bottom-4 right-4 bg-black/70 text-white text-sm font-medium px-3 py-1 rounded">
          {currentIndex + 1} / {allMedia.length}
        </div>
      </div>

      {/* Thumbnails */}
      {allMedia.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {allMedia.map((media, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`gallery-thumbnail flex-shrink-0 relative ${
                index === currentIndex ? 'active' : ''
              }`}
              data-testid={`thumbnail-${index}`}
            >
              {media.type === 'video' ? (
                <>
                  <img
                    src={media.tipo === 'youtube' 
                      ? (media.thumbnail || `https://img.youtube.com/vi/${media.url.split('/').pop()}/hqdefault.jpg`)
                      : 'https://via.placeholder.com/80x60?text=Video'}
                    alt={`Video ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Play className="w-4 h-4 text-white" />
                  </div>
                </>
              ) : (
                <img
                  src={getImageUrl(media.src)}
                  alt={`${title} - Miniatura ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = defaultImage;
                  }}
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Image Zoom Modal */}
      <Dialog open={isZoomOpen} onOpenChange={setIsZoomOpen}>
        <DialogContent 
          className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-0 bg-black/95 border-none overflow-hidden"
          onPointerDownOutside={closeZoom}
        >
          {/* Controls */}
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomIn}
              className="bg-white/10 hover:bg-white/20 text-white rounded-full"
              data-testid="zoom-in"
            >
              <ZoomIn className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomOut}
              className="bg-white/10 hover:bg-white/20 text-white rounded-full"
              data-testid="zoom-out"
            >
              <ZoomOut className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={closeZoom}
              className="bg-white/10 hover:bg-white/20 text-white rounded-full"
              data-testid="zoom-close"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Zoom Level Indicator */}
          <div className="absolute top-4 left-4 z-10 bg-white/10 text-white text-sm px-3 py-1 rounded">
            {Math.round(zoomLevel * 100)}%
          </div>

          {/* Navigation */}
          {allMedia.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full z-10"
                onClick={goToPrev}
              >
                <ChevronLeft className="w-8 h-8" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full z-10"
                onClick={goToNext}
              >
                <ChevronRight className="w-8 h-8" />
              </Button>
            </>
          )}

          {/* Zoomable Image */}
          <div 
            className="w-full h-full flex items-center justify-center overflow-hidden"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
            style={{ cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
          >
            <img
              src={getImageUrl(currentMedia.type === 'image' ? currentMedia.src : defaultImage)}
              alt={`${title} - Zoom`}
              className="max-w-none select-none"
              style={{
                transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-out'
              }}
              draggable={false}
              onError={(e) => {
                e.target.src = defaultImage;
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Modal */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="max-w-4xl w-full p-0 bg-black border-none overflow-hidden">
          <div className="absolute top-4 right-4 z-10">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowVideoModal(false)}
              className="bg-white/10 hover:bg-white/20 text-white rounded-full"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="aspect-video w-full">
            {currentVideo?.tipo === 'youtube' ? (
              <iframe
                src={currentVideo.url}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Video de YouTube"
              />
            ) : currentVideo ? (
              <video
                src={getImageUrl(currentVideo.url)}
                className="w-full h-full"
                controls
                autoPlay
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
